/* ==========================================================================
   WD Collection v2 — keeping the mobile toolbar in step with the results
   --------------------------------------------------------------------------
   The Filters / Sort bar lives in layout/theme.liquid, ABOVE the collection
   section, because the theme wanted it at the very top of the page on touch.
   That puts it outside <product-facet>, and product-facet only re-renders its
   own two children — #facet-main and #facet-filters — when a facet changes.
   So everything on that bar goes stale the moment anything is filtered: the
   theme itself only ever patched one class on it by hand.

   Rather than re-render the bar, this mirrors it from the copy the section
   renders inside #facet-main, which the server does keep current:

     · the chip rail            ← .wd-toolbar .product-facet__active-list
     · "Filters (3)"            ← how many chips there are
     · "Sort: Featured"         ← #sort-by-selected-value

   One source of truth, server-rendered, so there is no second count to drift.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__wdCollectionV2) return;
  window.__wdCollectionV2 = true;

  const SOURCE = '#facet-main .wd-toolbar .product-facet__active-list';

  function sync() {
    const rail = document.getElementById('wd-sticky-chips');
    if (!rail) return;

    const source = document.querySelector(SOURCE);
    const chips = source ? source.querySelectorAll('.tag') : [];

    // innerHTML from a server-rendered node of our own section, not from
    // anything a shopper can influence.
    rail.innerHTML = source ? source.innerHTML : '';
    rail.hidden = chips.length === 0;

    const filters = document.querySelector('.mobile-toolbar__item--filters');
    if (filters) {
      filters.classList.toggle('has-filters', chips.length > 0);
      const label = filters.querySelector('.mobile-toolbar__item-label');
      if (label) {
        let count = label.querySelector('.mobile-toolbar__count');
        if (chips.length === 0) {
          if (count) count.remove();
        } else {
          if (!count) {
            count = document.createElement('span');
            count.className = 'mobile-toolbar__count';
            label.appendChild(count);
          }
          count.textContent = `(${chips.length})`;
        }
      }
    }

    const sortValue = document.getElementById('sort-by-selected-value');
    const mobileSortValue = document.getElementById('sort-by-selected-value-mobile');
    if (sortValue && mobileSortValue) {
      mobileSortValue.textContent = sortValue.textContent.trim();
    }

    // Chips just changed, so whether the rail overflows may have too.
    markPannable();
  }

  const start = () => {
    const main = document.getElementById('facet-main');
    if (!main) return;
    sync();
    if (window.MutationObserver) {
      // Called straight through rather than via requestAnimationFrame: rAF is
      // suspended while the document is hidden, and a background tab that gets
      // filtered (or a prerendered one) would come back showing stale chips.
      // Everything sync() writes to lives outside #facet-main, so it cannot
      // re-trigger the observer it is called from.
      new MutationObserver(sync).observe(main, { childList: true, subtree: true });
    }
  };

  /* ------------------------------------------------------------------------
     Dragging the chip rail
     ------------------------------------------------------------------------
     The rail is a horizontal scroller. On touch that is a swipe and needs no
     help, but with a mouse there is nothing to grab: the wheel scrolls the
     page rather than the rail, and a chip past the right edge is unreachable.
     So: click and drag to pan, and a vertical wheel over the rail moves it
     sideways.

     Listeners are delegated from document rather than bound per rail, because
     product-facet replaces the rail wholesale on every facet change and
     re-bound handlers would leak one set per filter click.
     ---------------------------------------------------------------------- */

  const RAIL = '[data-wd-toolbar] .product-facet__active-list';
  const DRAG_SLOP = 4; // px of movement before a click is treated as a drag

  let rail = null;
  let startX = 0;
  let startLeft = 0;
  let moved = 0;

  const scrollable = (el) => el && el.scrollWidth > el.clientWidth + 1;

  // Marks rails that actually overflow, so the grab cursor only appears when
  // there is somewhere to grab TO.
  const markPannable = () => {
    document.querySelectorAll(RAIL).forEach((el) => {
      el.classList.toggle('wd-pannable', scrollable(el));
    });
  };

  document.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.pointerType === 'touch') return;
    const el = e.target.closest(RAIL);
    if (!scrollable(el)) return;
    rail = el;
    startX = e.clientX;
    startLeft = el.scrollLeft;
    moved = 0;
    el.classList.add('wd-panning');
  });

  document.addEventListener('pointermove', (e) => {
    if (!rail) return;
    const dx = e.clientX - startX;
    moved = Math.max(moved, Math.abs(dx));
    rail.scrollLeft = startLeft - dx;
    if (moved > DRAG_SLOP) e.preventDefault();
  });

  const endDrag = () => {
    if (!rail) return;
    rail.classList.remove('wd-panning');
    rail = null;
  };

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  // A drag that ends over a chip's × must not also remove that filter.
  document.addEventListener(
    'click',
    (e) => {
      if (moved <= DRAG_SLOP) return;
      if (!e.target.closest(RAIL)) return;
      e.preventDefault();
      e.stopPropagation();
      moved = 0;
    },
    true
  );

  document.addEventListener(
    'wheel',
    (e) => {
      const el = e.target.closest(RAIL);
      if (!scrollable(el)) return;
      // Trackpads send deltaX themselves; only a vertical-only wheel needs
      // translating, and only while the rail has room left to move.
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const next = el.scrollLeft + e.deltaY;
      if (next < 0 || next > el.scrollWidth - el.clientWidth) return;
      el.scrollLeft = next;
      e.preventDefault();
    },
    { passive: false }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('resize', markPannable);
  document.addEventListener('DOMContentLoaded', markPannable);
  markPannable();
})();
